import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useQueryState } from 'nuqs';
import React, { useCallback, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { isLevitateHostedSite } from '../../../extViewmodels/sdk/models';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import {
	invalidateClientSiteQueries,
	useClientSitemapQuery,
	useClientSitePendingChangesQuery,
	useClientSiteQuery,
} from '../../../queries/Website/useClientSiteQuery';
import { AppBarNavigationItemPortalDestinationId } from '../../components-v2/AppLayout/components/AppBar';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButton,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { SparklesIcon } from '../../components/svgs/icons/SparklesIcon';
import { Portal } from '../../components/Portal';
import { styleSheet as s } from './styles';
import { WebsiteAssistantChat, WebsiteAssistantChatHandle } from './WebsiteAssistantChat';
import { WebsiteBreadcrumbs } from './WebsiteBreadcrumbs';
import { PreviewViewport, WebsitePreview } from './WebsitePreview';

/**
 * The site editor: the account's own site in a read-only preview, with Lev beside it. Every change is
 * described in chat and applied by the agent; the page's only jobs are to show the draft, keep the frame current
 * after each change, and tell the agent which page is open.
 */
const WEBSITE_ASSISTANT_DRAWER_VALUE = 'website-assistant';

export function WebsitePage() {
	const userSession = useUserSession();
	const hosted = isLevitateHostedSite(userSession.account.features?.websites);

	const siteQuery = useClientSiteQuery({ enabled: hosted });
	const sitemapQuery = useClientSitemapQuery({ enabled: hosted && Boolean(siteQuery.data) });
	const pendingQuery = useClientSitePendingChangesQuery({ enabled: hosted && Boolean(siteQuery.data) });

	// The page in view lives in the route (#/website?page=/about) so the browser's back and forward buttons step
	// through pages, a refresh lands where you were, and a page can be deep-linked. User navigation pushes;
	// agent-driven jumps replace, so stepping back doesn't retrace the assistant's moves.
	const [pageParam, setPageParam] = useQueryState('page');
	const pagePath = normalizePagePath(pageParam);
	const setPagePath = (path: string, replace = false) => {
		const normalized = normalizePagePath(path);
		if (normalized === pagePath) {
			return;
		}
		setPageParam(normalized === '/' ? null : normalized, { history: replace ? 'replace' : 'push' });
	};
	const chatRef = useRef<WebsiteAssistantChatHandle>(null);
	// Lev is the point of this screen, so the drawer opens with it rather than restoring whatever drawer was last
	// open elsewhere in the app.
	const [activeDrawer, setActiveDrawer] = useState(WEBSITE_ASSISTANT_DRAWER_VALUE);
	const [reloadKey, setReloadKey] = useState(0);
	const [viewport, setViewport] = useState<PreviewViewport>('desktop');

	// No homepage fallback: an unresolved path used to mean a vague hint to the agent, but it now addresses an
	// element index, and pairing that with the wrong page's id edits the wrong page.
	const currentPageId = sitemapQuery.data?.find(n => n.resolvedUrl === pagePath)?.pageId;

	// From a change card: navigate to the page that changed and reload the frame, and refresh the pending banner.
	// Announced-on-mount cards (userInitiated false) fire once per card, including on history load — the reload
	// is harmless there and means a returning user always sees the current draft.
	const handleWebsiteChange = useCallback(
		(change: Api.WebsiteChangeToolCall) => {
			if (change.pagePath) {
				setPagePath(change.pagePath, true);
			}
			setReloadKey(k => k + 1);
			invalidateClientSiteQueries();
		},
		[setPagePath]
	);

	if (!hosted) {
		return <Redirect to='/dashboard' />;
	}

	if (siteQuery.isLoading) {
		return (
			<div className={css(s.empty)}>
				<LoadingSpinner />
			</div>
		);
	}

	if (siteQuery.isError) {
		return (
			<div className={css(s.empty)} role='alert'>
				<div className={css(s.emptyTitle)}>We couldn’t load your website</div>
				<div>Refresh to try again, or contact us if it keeps happening.</div>
			</div>
		);
	}

	if (!siteQuery.data) {
		return (
			<div className={css(s.empty)}>
				<div className={css(s.emptyTitle)}>Your website is being built</div>
				<div>It will show up here as soon as the Levitate team has set it up.</div>
			</div>
		);
	}

	// Picking an element aims a content change, which managePages alone doesn't grant — it covers page rename, SEO
	// and deletion, and reaches no element. The lock is already subtracted from every verb by the API.
	const { access } = siteQuery.data;
	const canEdit = access.editContent || access.editElements || access.managePages;
	const canSelectElements = Boolean(currentPageId) && (access.editContent || access.editElements);

	return (
		<div className={css(s.page)}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<WebsiteBreadcrumbs sitemap={sitemapQuery.data} pagePath={pagePath} />
			</Portal>
			<WebsitePreview
				site={siteQuery.data}
				sitemap={sitemapQuery.data}
				pendingChanges={pendingQuery.data}
				pagePath={pagePath}
				onPagePathChange={path => setPagePath(path)}
				reloadKey={reloadKey}
				viewport={viewport}
				onViewportChange={setViewport}
				canSelectElements={canSelectElements}
				onElementSubmit={(selection, text) => Boolean(chatRef.current?.submitElementEdit(selection, text))}
			/>
			<AppDrawer value={activeDrawer} onValueChange={setActiveDrawer}>
				<StickyAppDrawerContent value={WEBSITE_ASSISTANT_DRAWER_VALUE}>
					<WebsiteAssistantChat
						ref={chatRef}
						currentPageId={currentPageId}
						canEdit={canEdit}
						onWebsiteChange={handleWebsiteChange}
					/>
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButton value={WEBSITE_ASSISTANT_DRAWER_VALUE} tooltip='Edit with Lev'>
						<SparklesIcon />
					</AppDrawerToolbarButton>
				</AppDrawerToolbar>
			</AppDrawer>
		</div>
	);
}

/** `/`, `/about`, `/services/roofing` — no trailing slash, always a leading one, never a query or fragment. */
function normalizePagePath(value: string | null | undefined): string {
	const trimmed = (value ?? '').split(/[?#]/)[0].trim().replace(/\/+$/, '');
	if (!trimmed) {
		return '/';
	}
	return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}
